/**
 * How every game earns its stars, built into the app (2026-10-29). This used
 * to be each community's KZ_GoldStar sheet, exported and converted on every
 * change; the sheets were all the same apart from mistakes, so Spencer moved
 * it here: one table for every community, changed with a code change.
 *
 * Each game has one or two ratings. A rating's `type` says what's measured,
 * and its thresholds (per difficulty) are what 1, 2 and 3 stars need:
 *
 * - "accuracy": right tries / all tries, at least the threshold (0..1)
 * - "time":     seconds for the whole game, at most the threshold
 * - "correct":  the score (right minus wrong), at least the threshold
 *
 * The rebuilt games end a round after a set number of right answers (Wordless,
 * Spot Them All, Solve It, Crocodile...), so how many right answers is the
 * same for everyone; what differs is how many tries it took and how long. So
 * those are scored on accuracy first and time second (Spencer's call).
 *
 * Time thresholds come from a brisk play-through of each game (the fastest
 * realistic time, "floor", by a tester who knew every answer): 3 stars at ~3x
 * the floor, 2 at ~4x, 1 at ~5.5x; the math games (Solve It, Crocodile) 4.5x /
 * 6x / 8x, since a child works each one out. Every time was loosened by half
 * on 2026-11-02 (Spencer: this is for kids). The floors are noted beside each
 * game; players' times aren't collected anywhere, so these can't be checked
 * against real play.
 *
 * `scoreType` picks what the game's bottom badges show: "normal" a clock,
 * "time" the time, "timeAndHits" both, "story" nothing (Story Book).
 */

const ACCURACY = { easy: [0.5, 0.75, 0.9], medium: [0.5, 0.75, 0.9], hard: [0.5, 0.75, 0.9] };
const same = (thresholds) => ({ easy: thresholds, medium: thresholds, hard: thresholds });

/** Game -> { scoreType, ratings }, keyed by the game's squished type (findSquishName). */
export const GAME_SCORING = {
  BubblePop: {
    scoreType: "timeAndHits",
    ratings: [
      { type: "accuracy", ...ACCURACY },
      { type: "time", easy: [375, 300, 225], medium: [350, 280, 200], hard: [330, 255, 180] },
    ],
  },
  // rebuilt 2026-10-29 (tap to count). Floors: 100s / 109s / 116s. Medium
  // and Hard are searched with a magnifying glass, and the floors come from a
  // tester who knew where everything was, so they get far more room: Medium
  // 4.5x / 6.5x / 8.5x, Hard 5.25x / 7.5x / 10.5x
  Counting: {
    scoreType: "timeAndHits",
    ratings: [
      { type: "accuracy", ...ACCURACY },
      { type: "time", easy: [560, 410, 300], medium: [940, 700, 495], hard: [1215, 870, 610] },
    ],
  },
  // Spot Them All (its type is still "Good Guess"). Floors: 77s / ~83s / 89s
  GoodGuess: {
    scoreType: "timeAndHits",
    ratings: [
      { type: "accuracy", ...ACCURACY },
      { type: "time", easy: [435, 315, 230], medium: [465, 345, 250], hard: [500, 370, 270] },
    ],
  },
  // floors 41s / 63s (hard): a memory game, the old sheet's numbers fit
  Matching: {
    scoreType: "time",
    ratings: [{ type: "time", easy: [200, 150, 110], medium: [330, 225, 180], hard: [420, 330, 260] }],
  },
  // timed rounds: how many critters whacked counts (Denaakke and Salish's
  // numbers; three sheets asked for 68, which the game can't reach)
  WhackAMole: {
    scoreType: "normal",
    ratings: [{ type: "correct", ...same([12, 20, 25]) }, { type: "accuracy", ...ACCURACY }],
  },
  // floors 24s / ~31s / 38s
  WordToImage: {
    scoreType: "timeAndHits",
    ratings: [
      { type: "accuracy", ...ACCURACY },
      { type: "time", easy: [135, 100, 75], medium: [170, 130, 90], hard: [220, 160, 110] },
    ],
  },
  // floors 79s / ~81s / 83s
  Wordless: {
    scoreType: "timeAndHits",
    ratings: [
      { type: "accuracy", ...ACCURACY },
      { type: "time", easy: [440, 320, 240], medium: [460, 340, 240], hard: [465, 345, 250] },
    ],
  },
  // rebuilt 2026-11-02 (catching). Floors ~105s / 83s / 79s. A missed picture
  // costs no accuracy, only time, so the time rating is what rewards catching
  ItemFall: {
    scoreType: "timeAndHits",
    ratings: [
      { type: "accuracy", ...ACCURACY },
      { type: "time", easy: [590, 435, 315], medium: [465, 345, 250], hard: [440, 320, 240] },
    ],
  },
  // floors 81s / ~86s / 92s (a tester who knew every answer)
  SolveIt: {
    scoreType: "timeAndHits",
    ratings: [
      { type: "accuracy", ...ACCURACY },
      { type: "time", easy: [670, 490, 370], medium: [710, 520, 390], hard: [760, 555, 410] },
    ],
  },
  // floors 69s / ~75s / 81s
  Crocodile: {
    scoreType: "timeAndHits",
    ratings: [
      { type: "accuracy", ...ACCURACY },
      { type: "time", easy: [570, 410, 310], medium: [615, 450, 340], hard: [670, 490, 370] },
    ],
  },
  FillInTheBlank: { scoreType: "normal", ratings: [{ type: "accuracy", ...ACCURACY }] },
  History: { scoreType: "normal", ratings: [{ type: "accuracy", ...ACCURACY }] },
  StoryBook: { scoreType: "story", ratings: [] },
  // Stepping Stones (its type is still "Verb Activity"), rebuilt 2026-11-02.
  // Floors ~49s; the tester needed no time to think, so more stones get more room
  VerbActivity: {
    scoreType: "timeAndHits",
    ratings: [
      { type: "accuracy", ...ACCURACY },
      { type: "time", easy: [280, 200, 150], medium: [310, 225, 165], hard: [340, 250, 180] },
    ],
  },
  FromMeToYou: {
    scoreType: "timeAndHits",
    ratings: [{ type: "accuracy", ...ACCURACY }, { type: "time", ...same([350, 290, 230]) }],
  },
  Bingo: {
    scoreType: "timeAndHits",
    ratings: [{ type: "accuracy", ...ACCURACY }, { type: "time", ...same([350, 290, 230]) }],
  },
};

const LEVELS = { easy: "Easy", medium: "Medium", hard: "Hard" };

/**
 * The table as rows in the old sheet's shape (game, type, secondType,
 * scoreType, oneStarEasy ... threeStarTwoHard), which the star code and the
 * menus already read from the store as `goldStar`.
 */
export const goldStarRows = () =>
  Object.entries(GAME_SCORING).map(([game, { scoreType, ratings }]) => {
    const [first, second] = ratings;
    const row = { game, scoreType, type: first?.type ?? "none", secondType: second?.type ?? "none" };
    Object.entries(LEVELS).forEach(([level, Level]) => {
      ["one", "two", "three"].forEach((n, i) => {
        row[`${n}Star${Level}`] = first ? first[level][i] : "";
        row[`${n}StarTwo${Level}`] = second ? second[level][i] : "";
      });
    });
    return row;
  });

/** A game's scoring, by its type ("Solve It", "Good Guess"...), or undefined. */
export const scoringFor = (gameType) =>
  GAME_SCORING[String(gameType ?? "").replaceAll(" ", "").replaceAll("-", "").replaceAll(/[0-9]/g, "")];

/** How many stars a game offers per difficulty: 3 a rating. */
export const starsPerDifficulty = (gameType) => 3 * Math.max(1, scoringFor(gameType)?.ratings.length ?? 1);
